import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import ReviewBlock from "./ReviewBlock";
import { Typography, Box } from "@mui/material";
import Rating from "@mui/material/Rating";
import { NotEnoughReting } from "./NotEnoughReting";
import api from "components/settings/api";

const UserRatingsDetailed = ({ userEmail }) => {
  const [userRatings, setUserRatings] = useState([]);
  const [randomReviews, setRandomReviews] = useState([]);
  const [hasRatings, setHasRatings] = useState(false);
  const [skillAvg, setSkillAvg] = useState("loading");
  const [teamAvg, setTeamAvg] = useState("loading");
  const [viewSkillAvg, setViewSkillAvg] = useState("loading");
  const [viewTeamAvg, setViewTeamAvg] = useState("loading");
  const [allReviewsOpen, setAllReviewsOpen] = useState(false);

  const fetchRatings = async () => {
    try {
      const res = await api.get(`/api/v1/ratings/rating/${userEmail}`);
      // Response data
      const { data } = res;
      // Set rating
      setUserRatings(res.data);

      // Determine if user has rating and review data
      if (data.length > 0) {
        setHasRatings(true);
      }
    } catch (err) {
      throw new Error(err);
    }
  };

  useEffect(() => {
    // Make sure props have passed email
    if (userEmail) {
      fetchRatings();
    }
  }, [userEmail]);

  useEffect(() => {
    if (userRatings.length > 0) {
      calculateAvg();
      setRandomReviews(getRandomReviews(userRatings));
    }
  }, [userRatings]);

  // randomize reviews and returns random reviews
  const getRandomReviews = (allReviews) => {
    let j, x, i;
    for (i = allReviews.length - 1; i > 0; i--) {
      j = Math.floor(Math.random() * (i + 1));
      x = allReviews[i];
      allReviews[i] = allReviews[j];
      allReviews[j] = x;
    }
    return allReviews;
  };

  const calculateAvg = () => {
    // Sum the skillset ratings
    const skillsetRatingSum = userRatings.reduce((sum, ratingObj) => {
      return sum + parseFloat(ratingObj.skillset_rating);
    }, 0);

    // Sum the team ratings
    const teamRatingSum = userRatings.reduce((sum, ratingObj) => {
      return sum + parseFloat(ratingObj.team_member_rating);
    }, 0);

    // Determine the averages
    const skillsetRatingAvg = skillsetRatingSum / userRatings.length;
    const teamRatingAvg = teamRatingSum / userRatings.length;

    // Set the string values in state to render
    setSkillAvg(skillsetRatingAvg);
    setTeamAvg(teamRatingAvg);

    setViewSkillAvg(`${skillsetRatingAvg.toFixed(1)}`);
    setViewTeamAvg(`${teamRatingAvg.toFixed(1)}`);
  };

  /*
  Block of reviews
  Additional reviews
  */
  const userAdditionalReviews = randomReviews.map((dataArr, idx) => {
    if (idx <= 9) {
      return null;
    } else {
      return (
        <ReviewBlock review={dataArr.review} key={idx} count={idx} />
      );
    }
  });

  const user10Reviews = randomReviews.map((dataArr, idx) => {
    if (idx > 9) {
      return null;
    } else {
      return (
        <ReviewBlock review={dataArr.review} key={idx} count={idx} />
      );
    }
  });

  const handleClick = () => {
    setAllReviewsOpen(!allReviewsOpen);
  };

  return (
    <section className="container--section_data_wrapper-one_col">
      <div className="container--user_profile_ratings">
        <div className="container--user_profile_rating average_rating_one">
          {skillAvg <= 2.4 ? (
            <hr className="header--profile_rating_teamRed" color="#E67C89" />
          ) : skillAvg >= 2.5 && skillAvg <= 3.4 ? (
            <hr className="header--profile_rating_teamRed" color="#ffeb3b" />
          ) : skillAvg >= 3.5 ? (
            <hr className="header--profile_rating_teamRed" color="#2eb790" />
          ) : skillAvg === undefined || skillAvg === null ? (
            <hr className="header--profile_rating_teamRed" color="#236ccc" />
          ) : (
            <hr className="header--profile_rating_teamRed" color="#236ccc" />
          )}
          <h3 className="header header--profile_rating_header">
            Average Skillset Rating
          </h3>
          {hasRatings ? (
            <Box component="fieldset" mb={3} borderColor="transparent">
              <Typography component="legend"></Typography>
              <div className="ratings--container">
                <Rating
                  name="half-rating"
                  value={skillAvg}
                  precision={0.5}
                  readOnly
                />
                <Typography component="span" className="ratings--user_team_avg">
                  {viewSkillAvg}
                </Typography>
              </div>
            </Box>
          ) : (
            <NotEnoughReting />
          )}
        </div>
        <div className="container--user_profile_rating average_rating_two">
          {teamAvg <= 2.4 ? (
            <hr className="header--profile_rating_teamRed" color="#E67C89" />
          ) : teamAvg >= 2.5 && teamAvg <= 3.4 ? (
            <hr className="header--profile_rating_teamRed" color="#ffeb3b" />
          ) : teamAvg >= 3.5 ? (
            <hr className="header--profile_rating_teamRed" color="#2eb790" />
          ) : teamAvg === undefined || teamAvg === null ? (
            <hr className="header--profile_rating_teamRed" color="#236ccc" />
          ) : (
            <hr className="header--profile_rating_teamRed" color="#236ccc" />
          )}
          <h3 className="header header--profile_rating_header">
            Average Teamplayer Rating
          </h3>
          {hasRatings ? (
            <Box component="fieldset" mb={3} borderColor="transparent">
              <Typography component="legend"></Typography>
              <div className="ratings--container">            
                <Rating
                  name="half-rating"
                  value={teamAvg}
                  precision={0.5}
                  readOnly
                />
                <Typography component="span" className="ratings--user_team_avg">
                  {viewTeamAvg}
                </Typography>
              </div>
            </Box>
          ) : (
            <NotEnoughReting />
          )}
        </div>
      </div>
      <div className="container--user_profile_reviews_wrapper">
        <h2 className="header new_review-header">Teammate Reviews</h2>
        <div className="container--user_profile_reviews">
          {hasRatings ? (
            <div>
              <div
                className={"additional-review_profile"}
              >
                {user10Reviews}
              </div>
              <div
                className={
                  allReviewsOpen
                    ? "additional-review_profile"
                    : "additional-review_profile_hidden"
                }
              >
                {userAdditionalReviews}
              </div>
              {randomReviews.length > 10 && 
                <div className="see-all-reviews_profile">
                  <button onClick={handleClick} aria-label="See all reviews">
                    {allReviewsOpen ? "Close some reviews" : "See all Reviews"}
                  </button>
                </div>
              }
            </div>
          ) : (
            <div className="container--user_profile_review-no_background_color">
              <p className="text text--user_unnamed_author">No reviews yet</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
export default UserRatingsDetailed;

UserRatingsDetailed.propTypes = {
  userEmail: PropTypes.string,
};
